import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { PageHeader } from "../components/PageHeader";
import { Section, Overline, AmfButton } from "../components/Primitives";
import { IMAGES, CONTACT } from "../data/content";
import { postSubmission } from "../lib/api";
import { useContent } from "../context/ContentContext";

const inputCls =
  "w-full bg-transparent border-0 border-b border-amf-navy/25 rounded-none px-0 py-3 font-lora text-amf-charcoal placeholder-amf-stone focus:outline-none focus:border-amf-burgundy transition-colors";

export default function Contact() {
  const { t } = useContent();
  const { hash } = useLocation();
  const formRef = useRef(null);
  const [form, setForm] = useState({
    type: "contact",
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  const prefill = (type, subject) => {
    setForm((f) => ({ ...f, type, subject }));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await postSubmission(form);
      toast.success("Thank you. Your message has been received.");
      setForm({ type: "contact", name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow={t("contact.header.eyebrow")}
        title={t("contact.header.title")}
        intro={t("contact.header.intro")}
        image={IMAGES.girlsStudy}
      />

      {/* GET IN TOUCH */}
      <Section className="bg-amf-cream" id="get-in-touch">
        <div ref={formRef} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 scroll-mt-24">
          <FadeIn className="lg:col-span-7">
            <Overline>Get In Touch</Overline>
            <h2 className="font-playfair text-3xl md:text-4xl text-amf-navy mt-3 mb-8">
              Send us a message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-7" data-testid="contact-form">
              <input
                data-testid="contact-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="Your name"
                className={inputCls}
              />
              <input
                data-testid="contact-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="Email address"
                className={inputCls}
              />
              <input
                data-testid="contact-subject"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="Subject"
                className={inputCls}
              />
              <textarea
                data-testid="contact-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                required
                rows={4}
                placeholder="Your message"
                className={`${inputCls} resize-none`}
              />
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="inline-flex items-center justify-center font-inter uppercase tracking-[0.18em] text-sm bg-amf-burgundy text-amf-cream px-8 py-3.5 hover:bg-[#c24e12] transition-colors disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </FadeIn>

          <FadeIn delay={0.12} className="lg:col-span-5">
            <div className="bg-amf-navy text-amf-cream p-9 md:p-10">
              <Overline light>Foundation Details</Overline>
              <ul className="mt-7 space-y-6">
                <li className="flex gap-4">
                  <Phone size={20} strokeWidth={1.5} className="text-amf-gold shrink-0 mt-1" />
                  <span className="font-lora text-amf-cream/90">{CONTACT.phone}</span>
                </li>
                <li className="flex gap-4">
                  <Mail size={20} strokeWidth={1.5} className="text-amf-gold shrink-0 mt-1" />
                  <a href={`mailto:${CONTACT.email}`} className="font-lora text-amf-cream/90 hover:text-amf-gold break-all">
                    {CONTACT.email}
                  </a>
                </li>
                <li className="flex gap-4">
                  <MapPin size={20} strokeWidth={1.5} className="text-amf-gold shrink-0 mt-1" />
                  <span className="font-lora text-amf-cream/90 leading-relaxed">{CONTACT.address}</span>
                </li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* PARTNER */}
      <Section className="bg-[#F4F6F8]" id="partner">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center scroll-mt-24">
          <FadeIn>
            <Overline>Partner With Us</Overline>
            <p className="font-lora text-lg text-amf-charcoal/85 mt-5 leading-relaxed">
              {t("contact.partner.text")}
            </p>
          </FadeIn>
          <FadeIn delay={0.12} className="lg:text-right">
            <button
              data-testid="partner-button"
              onClick={() => prefill("partner", "Partnership Enquiry")}
              className="inline-flex items-center justify-center font-inter uppercase tracking-[0.18em] text-sm border border-amf-navy text-amf-navy px-8 py-3.5 hover:bg-amf-navy hover:text-amf-cream transition-colors"
            >
              Become a Partner
            </button>
          </FadeIn>
        </div>
      </Section>

      {/* VOLUNTEER */}
      <Section className="bg-amf-cream" id="volunteer">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center scroll-mt-24">
          <FadeIn>
            <Overline>Volunteer</Overline>
            <p className="font-lora text-lg text-amf-charcoal/85 mt-5 leading-relaxed">
              {t("contact.volunteer.text")}
            </p>
          </FadeIn>
          <FadeIn delay={0.12} className="lg:text-right">
            <button
              data-testid="volunteer-button"
              onClick={() => prefill("volunteer", "Volunteer Enquiry")}
              className="inline-flex items-center justify-center font-inter uppercase tracking-[0.18em] text-sm border border-amf-navy text-amf-navy px-8 py-3.5 hover:bg-amf-navy hover:text-amf-cream transition-colors"
            >
              Join Our Volunteer Network
            </button>
          </FadeIn>
        </div>
      </Section>

      {/* DONATE */}
      <Section className="bg-amf-navy" id="donate">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center scroll-mt-24">
          <FadeIn>
            <Overline light>Donate</Overline>
            <p className="font-lora text-lg text-amf-cream/85 mt-5 leading-relaxed">
              {t("contact.donate.text")}
            </p>
          </FadeIn>
          <FadeIn delay={0.12} className="lg:text-right">
            <AmfButton to="/donate" data-testid="contact-donate-button">
              Donate Now
            </AmfButton>
          </FadeIn>
        </div>
      </Section>
    </div>
  );
}
